import React, { useState, useEffect, useRef } from 'react';
import { PrimaryButton, SecondaryButton } from '@/components/Buttons';
import { addToast, getMerchants } from '@/store/app/action';
import { useDispatch } from 'react-redux';
import Loader from '@/components/Loader';
import { 
  initiateCloverOAuth, 
  completeCloverOAuth, 
  disconnectClover,
  getCloverConnectionStatus 
} from '@/api/clover';

const CloverConnectContent = ({ onClose, isAlreadyConnected = false, onConnectionSuccess }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const [errorMessage, setErrorMessage] = useState('');
  const [popupWindow, setPopupWindow] = useState(null);
  const dispatch = useDispatch();
  const popupRef = useRef(null);

  // Check connection status on component mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Cleanup effect for popup closure interval
  useEffect(() => {
    return () => {
      // Clean up popup closure interval on unmount
      if (popupRef.current && popupRef.current.closureCheckInterval) {
        clearInterval(popupRef.current.closureCheckInterval);
      }
    };
  }, []);

  // Listen for messages from popup window
  useEffect(() => {
    const handleMessage = async (event) => {
      // Check if this is a Clover OAuth message
      if (!event.data || !event.data.type || !event.data.type.startsWith('CLOVER_OAUTH_')) {
        return;
      }

      // Verify the message is from our popup OR from the same origin
      const isValidSource = event.source === popupRef.current || 
                           event.origin === window.location.origin ||
                           event.source === window.opener;
      
      if (!isValidSource) {
        return;
      }

      if (event.data.type === 'CLOVER_OAUTH_SUCCESS') {
        await handleOAuthCallback(event.data.code, event.data.state, event.data.merchant_id);
      } else if (event.data.type === 'CLOVER_OAUTH_ERROR') {
        setConnectionStatus('error');
        setErrorMessage(event.data.error);
        setIsConnecting(false);
        dispatch(addToast({
          type: 'error',
          message: 'Clover connection failed: ' + event.data.error,
          timer: 5
        }));
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [dispatch]); // Add dispatch as dependency

  const checkConnectionStatus = async () => {
    try {
      const status = await getCloverConnectionStatus();
      if (status.connected) {
        setConnectionStatus('connected');
      }
    } catch (error) {
      // If status check fails, assume disconnected
    }
  };

  const handleOAuthCallback = async (code, state, merchant_id) => {
    setIsConnecting(true);
    setConnectionStatus('connecting');
    
    try {
      const result = await completeCloverOAuth(code, state, window.location.origin + '/clover-callback', merchant_id);
      
      if (result.success) {
        setConnectionStatus('connected');
        dispatch(addToast({
          type: 'success',
          message: `Successfully connected to Clover! ${merchant_id ? `Merchant ID: ${merchant_id}` : ''}`,
          timer: 5
        }));
        
        // Clean up popup closure interval
        if (popupRef.current && popupRef.current.closureCheckInterval) {
          clearInterval(popupRef.current.closureCheckInterval);
        }
        dispatch(getMerchants());
        
        // Refresh location details if callback provided
        if (onConnectionSuccess) {
          await onConnectionSuccess();
        }
        
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        throw new Error(result.error || 'Connection failed');
      }
    } catch (error) {
      setConnectionStatus('error');
      setErrorMessage(error.message);
      dispatch(addToast({
        type: 'error',
        message: 'Failed to connect to Clover: ' + error.message,
        timer: 5
      }));
    } finally {
      setIsConnecting(false);
    }
  };

  const initiateOAuth = async () => {
    setIsConnecting(true);
    setConnectionStatus('connecting');
    setErrorMessage('');

    try {
      // Generate a random state parameter for security
      const state = Math.random().toString(36).substring(2, 15);
      
      // Store state in sessionStorage for verification
      sessionStorage.setItem('clover_oauth_state', state);

      const result = await initiateCloverOAuth(state, window.location.origin + '/clover-callback');
      
      if (result.authUrl) {
        // Open popup window for OAuth
        
        // Calculate center position for popup
        const width = 500;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          result.authUrl,
          'clover_oauth',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
        );
        
        if (!popup) {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }

        setPopupWindow(popup);
        popupRef.current = popup;

        // Check if popup was closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            // Clean up references
            setPopupWindow(null);
            popupRef.current = null;
            
            if (connectionStatus === 'connecting') {
              setConnectionStatus('error');
              setErrorMessage('OAuth was cancelled - popup was closed');
              setIsConnecting(false);
              dispatch(addToast({
                type: 'error',
                message: 'Clover connection was cancelled - popup was closed',
                timer: 5
              }));
            }
          }
        }, 1000);

        // Store the interval ID for cleanup
        popupRef.current.closureCheckInterval = checkClosed;

      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      setConnectionStatus('error');
      setErrorMessage(error.message);
      setIsConnecting(false);
      dispatch(addToast({
        type: 'error',
        message: 'Failed to start Clover connection: ' + error.message,
        timer: 5
      }));
    }
  };

  const disconnectCloverAccount = async () => {
    try {
      await disconnectClover();
      setConnectionStatus('disconnected');
      dispatch(addToast({
        type: 'success',
        message: 'Successfully disconnected from Clover',
        timer: 5
      }));
    } catch (error) {
      dispatch(addToast({
        type: 'error',
        message: 'Failed to disconnect from Clover: ' + error.message,
        timer: 5
      }));
    }
  };

  const renderContent = () => {
    switch (connectionStatus) {
      case 'connecting':
        return (
          <div className="text-center py-8">
            <Loader />
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Connecting to Clover...
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Please complete the authorization in the popup window.
            </p>
            {!popupWindow && (
              <button
                onClick={initiateOAuth}
                className="mt-4 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-600"
              >
                Open Popup Window
              </button>
            )}
          </div>
        );

      case 'connected':
        return (
          <div className="text-center py-8">
            <div className="mb-4">
              <svg className="w-16 h-16 text-green-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
              Successfully Connected!
            </h3>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Your Clover POS system is now connected and ready to sync data.
            </p>
            <div className="flex gap-3 justify-center">
              <SecondaryButton onClick={disconnectCloverAccount}>
                Disconnect
              </SecondaryButton>
              <PrimaryButton onClick={onClose}>
                Continue
              </PrimaryButton>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-8">
            <div className="mb-4">
              <svg className="w-16 h-16 text-red-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
              Connection Failed
            </h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              {errorMessage || 'An error occurred while connecting to Clover.'}
            </p>
            <div className="flex gap-3 justify-center">
              <SecondaryButton onClick={onClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={initiateOAuth} disabled={isConnecting}>
                Try Again
              </PrimaryButton>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <div className="mb-6 relative">
              {isAlreadyConnected && (
                <div className="absolute top-0 right-0 bg-green-500 rounded-full p-2 shadow-lg">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
              <img 
                src="/src/pages/externalFeeds/settings/sections/POSConnection/logos/clover.svg" 
                alt="Clover Logo" 
                className="w-24 h-24 mx-auto mb-4"
              />
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
                Connect to Clover POS
              </h3>
              {isAlreadyConnected && (
                <div className="mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Already Connected
                  </span>
                </div>
              )}
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                Connect your Clover POS system to automatically sync inventory, orders, and customer data.
              </p>
            </div>
            
            <div className="bg-slate-50 dark:bg-theme-surface-accent rounded-lg p-4 mb-6 text-left">
              <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">
                What you'll get:
              </h4>
              <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                <li>• Real-time inventory synchronization</li>
                <li>• Automatic order import</li>
                <li>• Customer data integration</li>
                <li>• Product catalog sync</li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-left">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                🔒 Secure Connection:
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                This will open a secure popup window for Clover authorization. 
                Your credentials are never stored in our system.
              </p>
            </div>

            <div className="flex gap-3 justify-center">
              <SecondaryButton onClick={onClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton 
                onClick={initiateOAuth} 
                disabled={isConnecting}
                loading={isConnecting}
              >
                Connect to Clover
              </PrimaryButton>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="p-6">
      {renderContent()}
    </div>
  );
};

export default CloverConnectContent; 